export const pickDefined = (...values) => {
  for (const value of values) {
    if (value === 0 || value === false) return value;
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

export const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  return [value];
};

export const getCollection = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;

  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  for (const key of ['items', 'data', 'results', 'rows']) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  return [];
};

export const toTitleCase = (value) =>
  String(value || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());

export const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

export const formatCurrency = (currency, amount) => {
  const numericAmount = Number(amount || 0);
  const normalizedCurrency = String(currency || 'INR').toUpperCase();

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 2
    }).format(numericAmount);
  } catch {
    return `${normalizedCurrency} ${numericAmount.toFixed(2)}`;
  }
};

export const normalizePriceMap = (value) => {
  if (value instanceof Map) return Object.fromEntries(value.entries());
  if (!value || typeof value !== 'object') return {};

  return Object.entries(value).reduce((acc, [currency, amount]) => {
    const code = String(currency || '').trim().toUpperCase();
    const numericAmount = Number(amount);

    if (code && Number.isFinite(numericAmount)) {
      acc[code] = Number(numericAmount.toFixed(2));
    }

    return acc;
  }, {});
};

export const resolveProductImage = (product) =>
  pickDefined(
    product?.media?.heroImage,
    product?.heroImage,
    product?.media?.galleryImages?.[0],
    toArray(product?.galleryImages)[0]
  );

export const normalizeProduct = (product = {}) => {
  const priceByCurrency = normalizePriceMap(
    pickDefined(product?.priceByCurrency, product?.pricing?.priceByCurrency, product?.prices)
  );
  const defaultCurrency = String(
    pickDefined(product?.defaultCurrency, Object.keys(priceByCurrency)[0], 'INR')
  ).toUpperCase();

  return {
    ...product,
    id: product?._id || product?.id || product?.SKU || product?.sku,
    SKU: String(pickDefined(product?.SKU, product?.sku, '') || '').toUpperCase(),
    title: String(pickDefined(product?.title, product?.name, 'Untitled Product')),
    description: String(product?.description || ''),
    priceByCurrency,
    defaultCurrency,
    payNow: Boolean(pickDefined(product?.payNow, true)),
    isPublished: Boolean(pickDefined(product?.isPublished, product?.publishStatus, false)),
    isActive: Boolean(pickDefined(product?.isActive, true)),
    media: {
      heroImage: pickDefined(product?.media?.heroImage, product?.heroImage, null),
      galleryImages: toArray(
        pickDefined(product?.media?.galleryImages, product?.galleryImages, [])
      ).filter(Boolean)
    }
  };
};

export const resolveOrderAmount = (order) =>
  Number(
    pickDefined(
      order?.amount?.total,
      order?.amount?.grandTotal,
      order?.amount,
      order?.totalAmount,
      order?.total,
      order?.payment?.amount,
      0
    )
  );

export const resolveOrderCurrency = (order) =>
  String(
    pickDefined(
      order?.currency,
      order?.amount?.currency,
      order?.payment?.currency,
      order?.customProduct?.defaultCurrency,
      'INR'
    )
  ).toUpperCase();

export const normalizeOrder = (order = {}) => {
  const customProduct = normalizeProduct(
    pickDefined(order?.customProduct, order?.product, order?.item, {})
  );

  return {
    ...order,
    id: order?._id || order?.id || order?.orderId,
    customProduct,
    customerName: pickDefined(
      order?.customerName,
      order?.customer?.name,
      order?.user?.name,
      order?.billingAddress?.name,
      order?.shippingAddress?.name
    ),
    customerEmail: pickDefined(
      order?.customerEmail,
      order?.customer?.email,
      order?.user?.email,
      order?.billingAddress?.email
    ),
    paymentStatus: String(
      pickDefined(order?.payment?.status, order?.paymentStatus, order?.status, 'pending')
    ),
    currency: resolveOrderCurrency(order),
    totalAmount: resolveOrderAmount(order)
  };
};

export const resolveInvoiceAmount = (invoice) =>
  Number(
    pickDefined(
      invoice?.amount?.total,
      invoice?.amount,
      invoice?.totalAmount,
      invoice?.total,
      invoice?.grandTotal,
      invoice?.order?.amount?.total,
      0
    )
  );

export const resolveInvoiceCurrency = (invoice) =>
  String(
    pickDefined(
      invoice?.currency,
      invoice?.amount?.currency,
      invoice?.order?.currency,
      invoice?.customProduct?.defaultCurrency,
      'INR'
    )
  ).toUpperCase();

export const normalizeInvoice = (invoice = {}) => {
  const order = normalizeOrder(pickDefined(invoice?.order, invoice?.customProductOrder, {}));
  const customProduct = normalizeProduct(
    pickDefined(invoice?.customProduct, order?.customProduct, {})
  );

  return {
    ...invoice,
    id: invoice?._id || invoice?.id || invoice?.invoiceId,
    invoiceNumber: pickDefined(
      invoice?.invoiceNumber,
      invoice?.number,
      invoice?.invoiceNo,
      invoice?.invoiceId
    ),
    order,
    customProduct,
    currency: resolveInvoiceCurrency(invoice),
    totalAmount: resolveInvoiceAmount(invoice),
    status: String(
      pickDefined(invoice?.status, invoice?.paymentStatus, invoice?.invoiceStatus, 'generated')
    )
  };
};

export const toPriceRows = (priceByCurrency = {}) => {
  const entries = Object.entries(normalizePriceMap(priceByCurrency));
  if (!entries.length) return [{ currency: 'INR', amount: '' }];

  return entries.map(([currency, amount]) => ({
    currency,
    amount: String(amount)
  }));
};

export const priceRowsToMap = (rows = []) =>
  rows.reduce((acc, row) => {
    const currency = String(row?.currency || '').trim().toUpperCase();
    const amount = Number(row?.amount);

    if (currency && Number.isFinite(amount) && amount >= 0) {
      acc[currency] = Number(amount.toFixed(2));
    }

    return acc;
  }, {});

export const splitLines = (value) =>
  String(value || '')
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
